'use client'

import { useState } from 'react'
import { formatPrice, formatChange } from '@/lib/formatters'

interface QuarterData {
  label: string
  open: number
  close: number
  high: number
  low: number
  return: number
  sparkline: number[] // normalized 0-1 values for SVG
}

interface YearData {
  year: number
  open: number
  close: number
  high: number
  low: number
  return: number
  sparkline: number[] // normalized 0-1 values for SVG
  quarters: QuarterData[]
}

interface Props {
  years: YearData[]
  globalLow: number
  globalHigh: number
}

function formatCompact(price: number): string {
  if (price >= 1000) return `$${(price / 1000).toFixed(1)}K`
  if (price >= 1) return `$${price.toFixed(0)}`
  return `$${price.toFixed(4)}`
}

// Generate SVG polyline from normalized values
function sparklinePath(values: number[], w: number, h: number, pad = 2): string {
  if (values.length < 2) return ''
  return values
    .map((v, i) => {
      const x = pad + (i / (values.length - 1)) * (w - pad * 2)
      const y = pad + (1 - v) * (h - pad * 2)
      return `${x},${y}`
    })
    .join(' ')
}

function sparklineArea(values: number[], w: number, h: number, pad = 2): string {
  if (values.length < 2) return ''
  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2)
    const y = pad + (1 - v) * (h - pad * 2)
    return `${x},${y}`
  })
  const first = pts[0].split(',')[0]
  const last = pts[pts.length - 1].split(',')[0]
  return `M${pts.join(' ')} L${last},${h} L${first},${h} Z`
}

export function YearlyPerformance({ years, globalLow, globalHigh }: Props) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const globalRange = globalHigh - globalLow || 1

  const toggle = (year: number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(year)) next.delete(year)
      else next.add(year)
      return next
    })
  }

  // Best/worst for highlighting
  const best = years.length > 2 ? years.reduce((a, b) => (b.return > a.return ? b : a)) : null
  const worst = years.length > 2 ? years.reduce((a, b) => (b.return < a.return ? b : a)) : null

  // Bar chart scaling
  const maxAbsReturn = Math.max(...years.map((y) => Math.abs(y.return)), 1)

  return (
    <div>
      {/* ═══ BAR CHART ═══ */}
      <div className="border border-zinc-800 px-4 py-5 mb-4">
        <div className="flex items-end justify-center h-[140px] relative">
          {/* Zero line */}
          <div className="absolute left-0 right-0 top-[70px] h-px bg-zinc-700" />
          <span className="absolute left-1 top-[60px] text-zinc-400 text-[9px]">0%</span>

          {years.map((ys) => {
            const isPos = ys.return >= 0
            const barH = Math.max((Math.abs(ys.return) / maxAbsReturn) * 54, 4)

            return (
              <div key={ys.year} className="flex flex-col items-center flex-1 max-w-[100px] relative h-full">
                {isPos ? (
                  <>
                    <span
                      className="text-green-500 text-[11px] font-bold tabular-nums absolute text-center w-full"
                      style={{ top: `${70 - barH - 16}px` }}
                    >
                      {formatChange(ys.return)}
                    </span>
                    <div
                      className="absolute w-10 sm:w-12 left-1/2 -translate-x-1/2 bg-green-500/20 border-t-2 border-green-500"
                      style={{ top: `${70 - barH}px`, height: `${barH}px` }}
                    />
                  </>
                ) : (
                  <>
                    <div
                      className="absolute w-10 sm:w-12 left-1/2 -translate-x-1/2 bg-red-500/20 border-b-2 border-red-500"
                      style={{ top: '71px', height: `${barH}px` }}
                    />
                    <span
                      className="text-red-500 text-[11px] font-bold tabular-nums absolute text-center w-full"
                      style={{ top: `${71 + barH + 2}px` }}
                    >
                      {formatChange(ys.return)}
                    </span>
                  </>
                )}
              </div>
            )
          })}
        </div>
        {/* Year labels — separate row, never overlaps */}
        <div className="flex justify-center">
          {years.map((ys) => (
            <div key={ys.year} className="flex-1 max-w-[100px] text-center">
              <span className="text-zinc-400 text-xs tabular-nums">{ys.year}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ TABLE ═══ */}
      <div className="border border-zinc-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-800">
              <th className="text-left text-zinc-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">
                Year
              </th>
              <th className="text-right text-zinc-400 text-xs font-medium px-4 py-3 uppercase tracking-wider hidden sm:table-cell">
                Open
              </th>
              <th className="text-right text-zinc-400 text-xs font-medium px-4 py-3 uppercase tracking-wider hidden sm:table-cell">
                Close
              </th>
              <th className="text-right text-zinc-400 text-xs font-medium px-4 py-3 uppercase tracking-wider">
                Return
              </th>
              <th className="text-zinc-400 text-xs font-medium px-2 lg:px-4 py-3 uppercase tracking-wider w-[80px] sm:w-[120px]">
                Range
              </th>
              <th className="text-zinc-400 text-xs font-medium px-4 py-3 uppercase tracking-wider w-[90px] sm:w-[130px] hidden sm:table-cell">
                Price path
              </th>
            </tr>
          </thead>
          {years.map((ys) => {
              const isOpen = expanded.has(ys.year)
              const isBest = best && ys.year === best.year
              const isWorst = worst && ys.year === worst.year
              const isPos = ys.return >= 0
              const color = isPos ? '#22c55e' : '#ef4444'
              const fillColor = isPos ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)'
              const rangeLeft = ((ys.low - globalLow) / globalRange) * 100
              const rangeWidth = ((ys.high - ys.low) / globalRange) * 100

              return (
                <tbody key={ys.year}>
                  {/* Year row */}
                  <tr
                    onClick={() => toggle(ys.year)}
                    className={`border-b border-zinc-800/50 cursor-pointer transition-colors hover:bg-zinc-900/40 ${
                      isBest ? 'bg-green-500/[0.04]' : isWorst ? 'bg-red-500/[0.04]' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-white text-sm font-medium whitespace-nowrap">
                      <svg
                        className={`inline-block w-3 h-3 mr-2 align-middle transition-transform ${
                          isOpen ? 'rotate-90' : ''
                        }`}
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="white"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M4.5 2.5L8 6L4.5 9.5" />
                      </svg>
                      <span className="tabular-nums align-middle">{ys.year}</span>
                    </td>
                    <td className="px-4 py-3 text-zinc-300 text-sm text-right tabular-nums hidden sm:table-cell">
                      {formatPrice(ys.open)}
                    </td>
                    <td className="px-4 py-3 text-zinc-300 text-sm text-right tabular-nums hidden sm:table-cell">
                      {formatPrice(ys.close)}
                    </td>
                    <td
                      className={`px-4 py-3 text-sm text-right tabular-nums font-semibold ${
                        isPos ? 'text-green-500' : 'text-red-500'
                      }`}
                    >
                      {formatChange(ys.return)}
                    </td>
                    <td className="px-2 lg:px-4 py-3">
                      <div className="w-full h-1.5 bg-zinc-800/50 relative">
                        <div
                          className={`absolute h-1.5 ${isPos ? 'bg-green-500/25' : 'bg-red-500/25'}`}
                          style={{ left: `${rangeLeft}%`, width: `${Math.max(rangeWidth, 2)}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-zinc-400 text-[9px] tabular-nums">
                          {formatCompact(ys.low)}
                        </span>
                        <span className="text-zinc-400 text-[9px] tabular-nums">
                          {formatCompact(ys.high)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2 hidden sm:table-cell">
                      <svg width={130} height={32} viewBox="0 0 130 32">
                        <path d={sparklineArea(ys.sparkline, 130, 32)} fill={fillColor} />
                        <polyline
                          points={sparklinePath(ys.sparkline, 130, 32)}
                          fill="none"
                          stroke={color}
                          strokeWidth={1.5}
                          strokeLinejoin="round"
                        />
                      </svg>
                    </td>
                  </tr>

                  {/* Quarter rows */}
                  {isOpen &&
                    ys.quarters.map((q) => {
                      const qPos = q.return >= 0
                      const qColor = qPos ? '#22c55e' : '#ef4444'
                      return (
                        <tr
                          key={q.label}
                          className="bg-zinc-950/80 border-b border-zinc-800/25"
                        >
                          <td className="px-4 py-2.5 pl-10 text-zinc-400 text-xs">{q.label}</td>
                          <td className="px-4 py-2.5 text-zinc-400 text-xs text-right tabular-nums hidden sm:table-cell">
                            {formatPrice(q.open)}
                          </td>
                          <td className="px-4 py-2.5 text-zinc-400 text-xs text-right tabular-nums hidden sm:table-cell">
                            {formatPrice(q.close)}
                          </td>
                          <td
                            className={`px-4 py-2.5 text-xs text-right tabular-nums font-semibold ${
                              qPos ? 'text-green-500' : 'text-red-500'
                            }`}
                          >
                            {formatChange(q.return)}
                          </td>
                          <td className="px-2 lg:px-4 py-2.5">
                            <div className="w-full h-1 bg-zinc-800/40 relative">
                              {(() => {
                                const yRange = ys.high - ys.low || 1
                                const qLeft = ((q.low - ys.low) / yRange) * 100
                                const qWidth = ((q.high - q.low) / yRange) * 100
                                return (
                                  <div
                                    className={`absolute h-1 ${qPos ? 'bg-green-500/20' : 'bg-red-500/20'}`}
                                    style={{
                                      left: `${qLeft}%`,
                                      width: `${Math.max(qWidth, 1)}%`,
                                    }}
                                  />
                                )
                              })()}
                            </div>
                            <div className="flex items-center justify-between mt-0.5">
                              <span className="text-zinc-400 text-[8px] tabular-nums">{formatCompact(q.low)}</span>
                              <span className="text-zinc-400 text-[8px] tabular-nums">{formatCompact(q.high)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 hidden sm:table-cell">
                            <svg width={130} height={16} viewBox="0 0 130 16">
                              <polyline
                                points={sparklinePath(q.sparkline, 130, 16)}
                                fill="none"
                                stroke={qColor}
                                strokeWidth={1}
                                strokeLinejoin="round"
                                opacity={0.6}
                              />
                            </svg>
                          </td>
                        </tr>
                      )
                    })}
                </tbody>
              )
            })}
        </table>
      </div>
    </div>
  )
}